import React, { FC, ReactNode, useRef } from "react";
import classNames from "classnames";
import { useSelector } from "react-redux";
import { NavLink, useNavigate } from "react-router-dom";

import { Button, Icon } from "../components/common";
import { ProfileMenu } from "../components/menu";
import { VerifyForm } from "../components/forms/settings";
import { ProfileSidebar } from "../components/sidebar/profile";
import { ROUTES } from "../routes";
import { selectCurrentUser } from "../store/reducers/user";
import { useLogoutMutation } from "../store/services/auth";
import { AccountType } from "../types/UserTypes";
import { isMobileMedia } from "../utils/media";
import { IntercomObserver } from "../components/observers";
import { useVerifyForm } from "../hooks/useVerifyForm";

type Props = {
  children?: ReactNode;
  sidebar?: ReactNode;
};

type HeaderProps = {
  displayName: string;
};

const Header: FC<HeaderProps> = ({ displayName }) => {
  const navigate = useNavigate();
  const [logout] = useLogoutMutation();

  const logoutHandler = async (e: any) => {
    await logout(e);
    navigate(ROUTES.AUTH_LOGIN);
  };

  return (
    <header className="fixed md:relative top-0 left-0 right-0 p-4 md:pt-8 md:pb-12 md:px-16 flex flex-col">
      <div className="flex flex-row items-center justify-between">
        <Button
          className="p-0 flex md:hidden"
          icon
          component={NavLink}
          to={ROUTES.HOME}
          variant={Button.variant.TERTIARY}
        >
          <Icon icon="Arrow-left" size={24} className="white" />
        </Button>

        <div className="grow text-base font-manrope text-center">
          {displayName}
        </div>

        <Button
          className="p-0 flex md:hidden"
          icon
          onClick={logoutHandler}
          variant={Button.variant.PRIMARY}
        >
          <Icon icon="Log-out" size={24} disableFill />
        </Button>
      </div>

      <ProfileMenu
        className="md:hidden mt-4"
        direction="horizontal"
        showIcons={false}
      />
    </header>
  );
};

const ProfileLayout: React.FC<Props> = ({ children }) => {
  const actionContainerRef = useRef<HTMLDivElement | null>(null);

  const { account_type, first_name, last_name, invoicing_data, kyc_complete } =
    useSelector(selectCurrentUser);

  const showVerifyForm = useVerifyForm(isMobileMedia());

  const displayName =
    account_type === AccountType.CONTRACTOR
      ? `${first_name} ${last_name}`
      : invoicing_data?.company_name;

  const containerClassNames =
    isMobileMedia() && showVerifyForm
      ? kyc_complete
        ? "mb-[143px]"
        : "mb-[270px]"
      : "pb-[68px]";

  return (
    <div className="w-full flex flex-row bg-white relative">
      <div className="hidden md:flex flex-col w-[300px] min-h-screen p-6 fixed left-0 top-0 z-[25]">
        <ProfileSidebar />
      </div>

      <div className="flex-1 basis flex flex-col h-screen">
        <Header displayName={displayName || ""} />

        <main
          className={classNames(
            "w-full md:w-[592px] h-full pt-4 md:pt-0 pb-0 md:pb-24 px-4 md:px-0 mt-[154px] md:my-0 mx-auto overflow-y-auto md:mb-0",
            containerClassNames
          )}
        >
          {children}
        </main>

        {showVerifyForm && (
          <div
            className="md:hidden fixed bottom-0 flex flex-col items-center w-full p-4 !z-[25] bg-white"
            ref={actionContainerRef}
          >
            <VerifyForm className="w-full" orientation="horizontal" />
          </div>
        )}
      </div>

      <IntercomObserver footer={actionContainerRef} />
    </div>
  );
};

export default ProfileLayout;
